import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { BarPlot } from '@mui/x-charts/BarChart';
import { LinePlot, MarkPlot } from '@mui/x-charts/LineChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsLegend } from '@mui/x-charts/ChartsLegend';
import type { DailyLog, ProjectTrack } from '../api/types';
import { buildProgressSeries } from './progress-chart/series';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Short id-ID date for the x-axis (e.g. "01 Jul"). */
function shortDate(iso: string): string {
  return new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: 'short' }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}

/**
 * Per-track progress chart (W2): daily increment bars + cumulative actual line
 * + linear target ramp (track planned dates, else items span) + blocked-day
 * markers at zero. All series math lives in progress-chart/series.ts (pure,
 * unit-tested); this component only renders.
 */
export function ProgressChart({
  track,
  logs,
  now,
}: {
  track: ProjectTrack;
  logs: DailyLog[];
  /** Injectable ISO date for deterministic tests; defaults to today. */
  now?: string;
}) {
  const theme = useTheme();
  const series = useMemo(
    () => buildProgressSeries(logs, track, now ?? todayIso()),
    [logs, track, now],
  );

  if (series.empty) {
    return (
      <Typography color="text.secondary" variant="body2" data-testid="progress-chart-empty">
        No progress logged yet.
      </Typography>
    );
  }

  const hasBlocked = series.blocked.some((b) => b !== null);

  return (
    <Box data-testid="progress-chart" sx={{ width: '100%' }}>
      <ChartContainer
        height={240}
        margin={{ top: 8, right: 16 }}
        xAxis={[
          {
            id: 'days',
            scaleType: 'band',
            data: series.dates,
            valueFormatter: (v: string) => shortDate(v),
            tickInterval: (_value: string, index: number) =>
              index % Math.max(1, Math.ceil(series.dates.length / 12)) === 0,
          },
        ]}
        yAxis={[{ id: 'units', label: track.unitLabel }]}
        series={[
          {
            type: 'bar',
            id: 'daily',
            label: `Daily (${track.unitLabel})`,
            data: series.daily,
            color: theme.palette.primary.light,
          },
          {
            type: 'line',
            id: 'cumulative',
            label: 'Cumulative',
            data: series.cumulative,
            color: theme.palette.primary.dark,
            showMark: false,
          },
          ...(series.target
            ? [
                {
                  type: 'line' as const,
                  id: 'target',
                  label: 'Target',
                  data: series.target,
                  color: theme.palette.text.secondary,
                  showMark: false,
                },
              ]
            : []),
          ...(hasBlocked
            ? [
                {
                  type: 'line' as const,
                  id: 'blocked',
                  label: 'Blocked day',
                  data: series.blocked,
                  color: theme.palette.error.main,
                  showMark: true,
                  connectNulls: false,
                },
              ]
            : []),
        ]}
        sx={{
          // dash the target ramp so it reads as "plan", not another actual
          '& .MuiLineElement-series-target': { strokeDasharray: '5 5' },
          '& .MuiLineElement-series-blocked': { display: 'none' }, // marks only
        }}
      >
        <BarPlot />
        <LinePlot />
        <MarkPlot />
        <ChartsXAxis axisId="days" />
        <ChartsYAxis axisId="units" />
        <ChartsTooltip />
        <ChartsLegend />
      </ChartContainer>
    </Box>
  );
}
